'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Discrete activity notice on login, signup, and dashboard.
 * Shows a compact withdrawal-style update with muted styling.
 */

const NAMES = [
  { first: 'Sarah', last: 'K.', country: 'Spain', flag: '🇪🇸' },
  { first: 'Marcus', last: 'J.', country: 'United States', flag: '🇺🇸' },
  { first: 'Aisha', last: 'R.', country: 'UAE', flag: '🇦🇪' },
  { first: 'David', last: 'O.', country: 'Nigeria', flag: '🇳🇬' },
  { first: 'Priya', last: 'S.', country: 'India', flag: '🇮🇳' },
  { first: 'Liam', last: 'B.', country: 'United Kingdom', flag: '🇬🇧' },
  { first: 'Sofia', last: 'M.', country: 'Italy', flag: '🇮🇹' },
  { first: 'Hiroshi', last: 'T.', country: 'Japan', flag: '🇯🇵' },
  { first: 'Anna', last: 'K.', country: 'Germany', flag: '🇩🇪' },
  { first: 'Lucas', last: 'P.', country: 'Brazil', flag: '🇧🇷' },
  { first: 'Mei', last: 'L.', country: 'Singapore', flag: '🇸🇬' },
  { first: 'Yusuf', last: 'A.', country: 'Turkey', flag: '🇹🇷' },
  { first: 'Elena', last: 'V.', country: 'Greece', flag: '🇬🇷' },
  { first: 'Carlos', last: 'G.', country: 'Mexico', flag: '🇲🇽' },
  { first: 'Olivia', last: 'W.', country: 'Australia', flag: '🇦🇺' },
  { first: 'Mohammed', last: 'H.', country: 'Saudi Arabia', flag: '🇸🇦' },
  { first: 'Isabelle', last: 'D.', country: 'France', flag: '🇫🇷' },
  { first: 'Daniel', last: 'F.', country: 'Canada', flag: '🇨🇦' },
  { first: 'Ngozi', last: 'E.', country: 'Ghana', flag: '🇬🇭' },
  { first: 'Klaus', last: 'M.', country: 'Austria', flag: '🇦🇹' },
];

const TIME_AGO_KEYS = [
  'user.socialProof.time.justNow',
  'user.socialProof.time.sec30',
  'user.socialProof.time.min1',
  'user.socialProof.time.min2',
  'user.socialProof.time.min3',
];

const AMOUNT_BUCKETS = [
  { min: 200, max: 980, step: 10, weight: 4 },
  { min: 1000, max: 4900, step: 50, weight: 6 },
  { min: 5000, max: 9500, step: 100, weight: 3 },
  { min: 10000, max: 24000, step: 250, weight: 2 },
  { min: 25000, max: 50000, step: 500, weight: 1 },
];

function weightedPick(items) {
  const total = items.reduce((s, x) => s + x.weight, 0);
  let r = Math.random() * total;
  for (const item of items) {
    r -= item.weight;
    if (r <= 0) return item;
  }
  return items[items.length - 1];
}

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function formatUsd(value, locale) {
  return new Intl.NumberFormat(locale.replace('_', '-'), {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function buildEvent(t, locale) {
  const person = pickOne(NAMES);
  const bucket = weightedPick(AMOUNT_BUCKETS);
  const steps = Math.floor((bucket.max - bucket.min) / bucket.step);
  const amount = bucket.min + randInt(0, steps) * bucket.step;
  const when = t(pickOne(TIME_AGO_KEYS));
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: `${person.first} ${person.last}`,
    initial: person.first.charAt(0).toUpperCase(),
    country: person.country,
    flag: person.flag,
    amountLabel: formatUsd(amount, locale),
    network: t('user.socialProof.network'),
    when,
  };
}

const VISIBLE_MS = 6000;
const MIN_GAP_MS = 22_000;
const MAX_GAP_MS = 42_000;
const INITIAL_DELAY_MS = 3_000;

export default function SocialProofPopup() {
  const { t, i18n } = useTranslation('panel');
  const locale = i18n.language || 'en';
  const [event, setEvent] = useState(null);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const pausedRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleNext = useCallback(
    (delay = randInt(MIN_GAP_MS, MAX_GAP_MS)) => {
      clearTimers();
      showTimerRef.current = setTimeout(() => {
        if (pausedRef.current) {
          scheduleNext(2_000);
          return;
        }
        setEvent(buildEvent(t, locale));
        hideTimerRef.current = setTimeout(() => {
          setEvent(null);
          scheduleNext();
        }, VISIBLE_MS);
      }, delay);
    },
    [clearTimers, t, locale]
  );

  useEffect(() => {
    scheduleNext(INITIAL_DELAY_MS);
    return clearTimers;
  }, [scheduleNext, clearTimers]);

  useEffect(() => {
    const onVisibility = () => {
      pausedRef.current = document.hidden;
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const dismiss = useCallback(() => {
    setEvent(null);
    scheduleNext();
  }, [scheduleNext]);

  const popup = useMemo(() => {
    if (!event) return null;
    return (
      <motion.div
        key={event.id}
        initial={{ opacity: 0, x: 8, y: -10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        exit={{ opacity: 0, x: 6, y: -6 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        role="status"
        aria-live="polite"
        onMouseEnter={() => {
          pausedRef.current = true;
          if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
          }
        }}
        onMouseLeave={() => {
          pausedRef.current = false;
          if (!hideTimerRef.current) {
            hideTimerRef.current = setTimeout(() => {
              setEvent(null);
              scheduleNext();
            }, 2_000);
          }
        }}
        className="pointer-events-auto w-54 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-border/60 bg-bg-card/30 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.35)] backdrop-blur-md sm:w-58"
      >
        <div className="flex items-start gap-2 px-2.5 py-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border/50 bg-bg-secondary/40 text-[10px] font-semibold text-text-muted">
            {event.initial}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-medium uppercase tracking-wider text-text-muted">
              {t('user.socialProof.label')}
            </p>
            <p className="mt-0.5 text-xs font-semibold tabular-nums text-text-primary">{event.amountLabel}</p>
            <p className="mt-0.5 truncate text-[10px] leading-tight text-text-muted">
              {event.network}
              <span className="text-text-muted/60"> · </span>
              {event.when}
            </p>
            <p className="mt-1 truncate text-[10px] text-text-muted/80">
              {event.name}
              <span className="text-text-muted/50"> · </span>
              {event.flag} {event.country}
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('common.dismissNotification')}
            className="-mr-0.5 -mt-0.5 shrink-0 rounded p-0.5 text-text-muted/70 transition hover:bg-bg-secondary/50 hover:text-text-primary"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </motion.div>
    );
  }, [event, dismiss, scheduleNext, t]);

  return (
    <div className="pointer-events-none fixed top-20 inset-e-4 z-100 sm:top-24 sm:inset-e-5">
      <AnimatePresence mode="wait">{popup}</AnimatePresence>
    </div>
  );
}
